import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';

export type IconCategory =
  | 'arrow'
  | 'building'
  | 'chart'
  | 'communication'
  | 'computer'
  | 'currency'
  | 'database'
  | 'design'
  | 'document'
  | 'e-commerce'
  | 'media'
  | 'other'
  | 'system'
  | 'filled'
  | 'legacy'; // for backward compatibility

// Legacy icon types for backward compatibility
export type LegacyIconType =
  | 'Wallet'
  | 'Payment'
  | 'International Payout'
  | 'Merchant'
  | 'Balance History'
  | 'Payout';

// New flexible icon type that supports any icon name
export type IconType = LegacyIconType | string;

export interface IconConfig {
  name: string;
  category: IconCategory;
  alt?: string;
  customClass?: string;
}

export interface IconMetadata {
  name: string;
  category: IconCategory;
  description?: string;
  keywords?: string[];
  path: string;
}

@Component({
  selector: 'lib-icons',
  standalone: true,
  imports: [AngularSvgIconModule],
  template: `
    <div class="icon-container" [attr.aria-label]="alt || iconName + ' icon'" role="img">
      <svg-icon [src]="iconSrc" class="icon-svg"></svg-icon>
    </div>
  `,
  styles: [
    `
      .icon-container {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        color: inherit;
        width: 24px;
        height: 24px;
      }

      .icon-svg {
        display: block;
        width: 100%;
        height: 100%;
        color: inherit;
      }

      .icon-svg ::ng-deep svg {
        width: 100%;
        height: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconsComponent {
  @Input() type: IconType = 'Wallet'; // Legacy support
  @Input() name: string = ''; // New flexible approach
  @Input() category: IconCategory = 'legacy';
  @Input() alt?: string;
  @Input() customClass?: string;

  // Legacy icon metadata for backward compatibility
  private legacyIconSources: Record<LegacyIconType, string> = {
    Wallet: 'assets/icons/wallet.svg',
    Payment: 'assets/icons/payment.svg',
    'International Payout': 'assets/icons/international-payout.svg',
    Merchant: 'assets/icons/merchant.svg',
    'Balance History': 'assets/icons/balance-history.svg',
    Payout: 'assets/icons/payout.svg',
  };

  // Category information
  private static categoryInfoMap: Record<
    IconCategory,
    { label: string; description: string; color: string }
  > = {
    arrow: {
      label: 'Arrows & Navigation',
      description: 'Directional indicators and navigation elements',
      color: '#3B82F6',
    },
    building: {
      label: 'Buildings & Places',
      description: 'Architectural and location-based icons',
      color: '#8B5CF6',
    },
    chart: {
      label: 'Charts & Analytics',
      description: 'Data visualization and analytics icons',
      color: '#10B981',
    },
    communication: {
      label: 'Communication',
      description: 'Messaging, email, and communication icons',
      color: '#F59E0B',
    },
    computer: {
      label: 'Computer & Technology',
      description: 'Technical and computing related icons',
      color: '#6B7280',
    },
    currency: {
      label: 'Currency & Finance',
      description: 'Financial symbols and currency icons',
      color: '#EF4444',
    },
    database: {
      label: 'Database & Storage',
      description: 'Data storage and database icons',
      color: '#06B6D4',
    },
    design: {
      label: 'Design & Creative',
      description: 'Design tools and creative elements',
      color: '#EC4899',
    },
    document: {
      label: 'Documents & Files',
      description: 'File types and document management',
      color: '#84CC16',
    },
    'e-commerce': {
      label: 'E-commerce & Shopping',
      description: 'Online shopping and commerce icons',
      color: '#F97316',
    },
    media: {
      label: 'Media & Entertainment',
      description: 'Multimedia and entertainment icons',
      color: '#8B5CF6',
    },
    other: {
      label: 'Other & Miscellaneous',
      description: 'Various utility and miscellaneous icons',
      color: '#6B7280',
    },
    system: {
      label: 'System & Interface',
      description: 'System controls and UI elements',
      color: '#374151',
    },
    legacy: {
      label: 'Legacy Icons',
      description: 'Legacy icons for backward compatibility',
      color: '#9CA3AF',
    },
    filled: {
      label: 'Legacy Icons',
      description: 'Legacy icons for backward compatibility',
      color: '#9CA3AF',
    },
  };

  get iconName(): string {
    return this.name || this.type;
  }

  get iconSrc(): string {
    // Handle legacy icons
    if (this.legacyIconSources[this.type as LegacyIconType] && this.name == '') {
      return this.legacyIconSources[this.type as LegacyIconType];
    }

    // Handle new categorized icons
    if (this.name && this.category !== 'legacy') {
      return `assets/icons/${this.category}/${this.name}.svg`;
    }

    // Fallback
    return this.legacyIconSources['Wallet'];
  }

  get iconClasses(): string[] {
    const iconIdentifier = this.name || this.type;
    const classes = ['icon', `icon-${iconIdentifier}`, `icon-category-${this.category}`];

    if (this.customClass) {
      classes.push(this.customClass);
    }

    return classes;
  }

  setIcon(name: string, category: IconCategory): void {
    this.name = name;
    this.category = category;
  }

  // Legacy method for backward compatibility
  setIconType(newType: IconType): void {
    this.type = newType;
  }

  /**
   * Get current icon category
   */
  get iconCategory(): IconCategory {
    return this.category;
  }

  /**
   * Get category information
   */
  get categoryInfo(): { label: string; description: string; color: string } {
    return IconsComponent.categoryInfoMap[this.category];
  }

  /**
   * Get all available categories
   */
  static getCategories(): IconCategory[] {
    return [
      'arrow',
      'building',
      'chart',
      'communication',
      'computer',
      'currency',
      'database',
      'design',
      'document',
      'e-commerce',
      'media',
      'other',
      'system',
      'legacy',
    ];
  }

  /**
   * Get category information
   */
  static getCategoryInfo(category: IconCategory): {
    label: string;
    description: string;
    color: string;
  } {
    return IconsComponent.categoryInfoMap[category];
  }

  /**
   * Get icon counts by category
   */
  static getCategoryCounts(): Record<IconCategory, number> {
    return {
      arrow: 293,
      building: 62,
      chart: 31,
      communication: 99,
      computer: 22,
      currency: 64,
      database: 37,
      design: 215,
      document: 146,
      'e-commerce': 41,
      media: 100,
      other: 355,
      system: 371,
      legacy: 6,
      filled: 1,
    };
  }

  /**
   * Get sample icons for each category (for documentation)
   */
  static getSampleIcons(): Record<IconCategory, string[]> {
    return {
      arrow: ['arrow-right', 'arrow-left', 'arrow-up', 'arrow-down', 'chevron-right'],
      building: ['building', 'home', 'building-bank', 'building-store', 'building-warehouse'],
      chart: ['chart-line', 'chart-bar', 'chart-pie', 'chart-area', 'chart-donut'],
      communication: ['mail', 'message', 'phone', 'message-circle', 'send'],
      computer: ['binary', 'cloud-computing', 'error-404', 'binary-tree'],
      currency: ['currency-dollar', 'currency-euro', 'currency-bitcoin', 'currency-yen'],
      database: ['database', 'server', 'cloud-data', 'backup'],
      design: ['palette', 'brush', 'color-picker', 'layout', 'grid'],
      document: ['file', 'file-text', 'file-pdf', 'folder', 'archive'],
      'e-commerce': ['shopping-cart', 'credit-card', 'package', 'truck', 'receipt'],
      media: ['play', 'pause', 'music', 'video', 'camera'],
      other: ['star', 'heart', 'bookmark', 'tag', 'bell'],
      system: ['settings', 'user', 'lock', 'key', 'shield'],
      legacy: ['Wallet', 'Payment', 'Merchant', 'Payout'],
      filled: ['circle-x-filled'],
    };
  }
}
